import React, { useEffect, useState, useMemo } from 'react';
import { Outlet, useParams, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import CookieConsent from './CookieConsent';
import ConstructionPage from '../pages/ConstructionPage';
import { I18nContext } from '../i18nContext';
import { getTranslations, mergeOverrides, isValidLocale, DEFAULT_LOCALE } from '../i18n';
import { api } from '../lib/api';
import { usePublicConfig } from '../lib/publicConfig';
import { useAuth } from '../lib/AuthContext';
import { isStaffRole } from '../lib/permissions';

const PROFILE_PATHS = ['mi-perfil', 'my-profile', 'seguridad', 'cambiar-contrasena', 'change-password'];

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <Loader2 className="animate-spin text-cmic-500" size={32} />
    </div>
  );
}

// Module-level cache so we fetch overrides only once per session.
let overridesCache = null;
let overridesPromise = null;

async function loadOverrides() {
  if (overridesCache) return overridesCache;
  if (overridesPromise) return overridesPromise;
  overridesPromise = api
    .get('/translations')
    .then((r) => {
      overridesCache = r.data || {};
      return overridesCache;
    })
    .catch(() => {
      overridesCache = {};
      return overridesCache;
    });
  return overridesPromise;
}

export function refreshTranslationOverrides() {
  overridesCache = null;
  overridesPromise = null;
}

export default function Layout() {
  const { lang } = useParams();
  const { pathname } = useLocation();
  const locale = lang && isValidLocale(lang) ? lang : DEFAULT_LOCALE;
  const [overrides, setOverrides] = useState(overridesCache);
  const publicConfig = usePublicConfig();
  const { user } = useAuth();

  useEffect(() => {
    let alive = true;
    if (!overridesCache) {
      loadOverrides().then((ov) => {
        if (alive) setOverrides(ov);
      });
    }
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Apply admin-managed site theme as CSS custom properties on :root so any
  // component can react to it (e.g. style="background: var(--cmic-primary)").
  useEffect(() => {
    let alive = true;
    api.get('/site-theme').then(({ data }) => {
      if (!alive || !data) return;
      const root = document.documentElement;
      if (data.primary_color) root.style.setProperty('--cmic-primary', data.primary_color);
      if (data.accent_color) root.style.setProperty('--cmic-accent', data.accent_color);
      if (data.heading_font) root.style.setProperty('--cmic-heading-font', data.heading_font);
      if (data.body_font) {
        root.style.setProperty('--cmic-body-font', data.body_font);
        root.style.fontFamily = data.body_font;
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const translations = useMemo(
    () => (overrides ? mergeOverrides(locale, overrides) : getTranslations(locale)),
    [locale, overrides]
  );

  if (!lang || !isValidLocale(lang)) {
    return <Navigate to={`/${DEFAULT_LOCALE}`} replace />;
  }

  // ── Demo / construction mode gate ──────────────────────────────────────
  // Direct token links sent by email (news/event preview) always work, even
  // while the site is in construction mode, so recipients can open them.
  const isDirectLink = pathname.includes('/preview/');
  if (publicConfig === null) return <FullScreenLoader />;
  if (publicConfig.maintenance_mode && !isDirectLink) {
    if (user === null) return <FullScreenLoader />; // auth still resolving
    const isStaff = isStaffRole(user && user.role);
    if (!isStaff) {
      if (user && user.role === 'interpreter') {
        // Members can only reach their own profile during the demo: the member
        // dashboard/security pages AND their own public directory profile.
        const onProfile = PROFILE_PATHS.some((p) => pathname.includes(p));
        const ownSlug = user.interpreter_slug;
        const isOwnPublicProfile = !!ownSlug && (
          pathname === `/${locale}/directorio/${ownSlug}` ||
          pathname === `/${locale}/directory/${ownSlug}`
        );
        if (!onProfile && !isOwnPublicProfile) return <Navigate to={`/${locale}/mi-perfil`} replace />;
      } else {
        // Not logged in → public construction landing.
        return <ConstructionPage />;
      }
    }
  }

  return (
    <I18nContext.Provider value={translations}>
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1">
          <Outlet />
        </main>
        <Footer />
      </div>
      <CookieConsent locale={locale} />
    </I18nContext.Provider>
  );
}
